"use client";

import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as z from "zod/v4";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";

import { printProxiesApi } from "./api";
import {
	LINK_REFUSALS,
	macSchema,
	normaliseMac,
	normaliseSerial,
	serialSchema,
} from "./validation";

const schema = z.object({ macAddress: macSchema, serialNumber: serialSchema });

/**
 * HA-939: link a print proxy to this account by the MAC address and serial number on its label. Typed values are normalised (case,
 * spaces, MAC separators) and checked before anything is sent; the four refusals are said in the label flow's words and never say
 * which value was wrong.
 */
export function LinkPrintProxyDialog({
	open,
	onOpenChange,
	tenant,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tenant: string;
}) {
	const queryClient = useQueryClient();
	const [refusal, setRefusal] = React.useState<string | null>(null);
	const link = useMutation(printProxiesApi.linkOptions(tenant));

	const form = useAppForm({
		defaultValues: { macAddress: "", serialNumber: "" },
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: { onDynamic: schema },
		onSubmit: async ({ value }) => {
			setRefusal(null);
			const result = await link.mutateAsync({
				macAddress: normaliseMac(value.macAddress)!,
				serialNumber: normaliseSerial(value.serialNumber)!,
			});

			if (result.success) {
				toast.success("Print proxy linked.");
				await queryClient.invalidateQueries({
					queryKey: [tenant, "print-proxies"],
				});
				handleClose(false);
			} else {
				setRefusal(
					(result.code ? LINK_REFUSALS[result.code] : undefined) ??
						(result.status === 429
							? "Too many attempts. Wait a few minutes and try again."
							: "Could not link the print proxy. Try again."),
				);
			}
		},
	});

	const handleClose = (o: boolean) => {
		if (!o) {
			form.reset();
			link.reset();
			setRefusal(null);
		}
		onOpenChange(o);
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Link print proxy</DialogTitle>
					<DialogDescription>
						Enter the MAC address and serial number shown on the label of the
						print proxy we sent you.
					</DialogDescription>
				</DialogHeader>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						void form.handleSubmit();
					}}
					className="space-y-4"
				>
					{refusal && (
						<Alert variant="destructive">
							<AlertCircle className="size-4" />
							<AlertDescription>{refusal}</AlertDescription>
						</Alert>
					)}

					<form.AppField name="macAddress">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>MAC address</field.FormLabel>
								<field.FormControl>
									<Input
										name={field.name}
										placeholder="e.g. 2c:cf:67:67:bb:5f"
										autoComplete="off"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="serialNumber">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Serial number</field.FormLabel>
								<field.FormControl>
									<Input
										name={field.name}
										placeholder="e.g. ff7b2a81100d598c"
										autoComplete="off"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => handleClose(false)}
							disabled={link.isPending}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={link.isPending}>
							{link.isPending && (
								<Loader2 className="mr-2 size-4 animate-spin" />
							)}
							Link
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
